import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import EngineScoringTestComponent from './EngineScoringTestComponent';
import { emptyObject } from 'utils/constants';
import { TEST_SCORING_GRID } from '../mutations';
import { useMutation, useSubscription } from 'hooks/apollo';
import { TEST_SCORING_GRID_COMPLETED, TEST_SCORING_GRID_FAILED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';

const defaultConfig = { page: 0, pageSize: 5, totalCount: 0, filter: '' };
const rowsPerPageOptions = [5, 10, 20];

const EngineScoringTestContainer = ({ engineScoringId }) => {
  const DEBOUNCE_DELAY = 50;
  const [scoringTestAttributesOnPage, setScoringTestAttributesOnPage] = useState([]);
  const [scoringTestResult, setScoringTestResult] = useState(emptyObject);
  const [paginationAndFilter, setPaginationAndFilter] = useState(defaultConfig);

  const [t] = useTranslation();
  const addToast = useToast();

  const [testScoringGrid] = useMutation(TEST_SCORING_GRID);

  useSubscription(TEST_SCORING_GRID_COMPLETED, {
    onData: ({
      data: {
        data: {
          testScoringGridCompleted: { scoringGridTestResult: data }
        }
      }
    }) => {
      setScoringTestResult(data);
      if (data) {
        setPaginationAndFilter(prevPaginationAndFilter => ({
          ...prevPaginationAndFilter,
          totalCount: data.attributes?.length
        }));
        if (data.attributes?.length > 0) setScoringTestAttributesOnPage(data.attributes.slice(0, defaultConfig.pageSize));
      }
      addToast(t('EngineScoringGrid.ScoringGridTestCompleted'), 'success');
    }
  });

  useSubscription(TEST_SCORING_GRID_FAILED, {
    onData: ({ data }) => {
      addToast(data.data.testScoringGridFailed.errorMessage, 'error');
    }
  });

  useEffect(() => {
    if (engineScoringId && scoringTestResult === emptyObject)
      testScoringGrid({
        variables: { input: { engineScoringId, entityIdentifierForTest: localStorage.entityIdentifierForTest } }
      });
  }, [engineScoringId, scoringTestResult, testScoringGrid]);

  const getItemsPerPage = (scoringTestResult, pageSize, page, filter) => {
    let current = page * pageSize;
    let scoringTestAttributesOnPage;
    let result;

    if (scoringTestResult && scoringTestResult.attributes) {
      if (filter !== '') {
        result = filterByValue(scoringTestResult.attributes, filter);
        scoringTestAttributesOnPage = result.slice(current, current + pageSize);
        setPaginationAndFilter({
          ...paginationAndFilter,
          totalCount: result.length,
          page: defaultConfig.page,
          filter: filter
        });
      } else {
        scoringTestAttributesOnPage = scoringTestResult.attributes && scoringTestResult.attributes.slice(current, current + pageSize);
        setPaginationAndFilter({
          ...paginationAndFilter,
          totalCount: scoringTestResult.attributes && scoringTestResult.attributes.length,
          page: defaultConfig.page,
          filter: filter
        });
      }
    } else
      setPaginationAndFilter({
        ...paginationAndFilter,
        filter: filter
      });

    return scoringTestAttributesOnPage;
  };

  const updatePageSize = (items, paginationAndFilter) => {
    const page = paginationAndFilter.page;
    const pageSize = paginationAndFilter.pageSize;
    const filter = paginationAndFilter.filter;
    setScoringTestAttributesOnPage(getItemsPerPage(items, pageSize, page, filter));
  };

  const onPageChange = page => {
    updatePageSize(scoringTestResult, { ...paginationAndFilter, page });
    setPaginationAndFilter({ ...paginationAndFilter, page });
  };

  const onRowsPerPageChange = size => {
    if (scoringTestResult.attributes.length < size) {
      updatePageSize(scoringTestResult, { ...paginationAndFilter, pageSize: size, page: defaultConfig.page });
      setPaginationAndFilter({ ...paginationAndFilter, pageSize: size, page: defaultConfig.page });
    } else {
      updatePageSize(scoringTestResult, { ...paginationAndFilter, pageSize: size });
      setPaginationAndFilter({ ...paginationAndFilter, pageSize: size });
    }
  };

  const onPaginationAndFiltersRefresh = () => {
    updatePageSize(scoringTestResult, {
      ...paginationAndFilter,
      pageSize: defaultConfig.pageSize,
      filter: defaultConfig.filter
    });
    setPaginationAndFilter({
      ...defaultConfig,
      totalCount: scoringTestResult ? scoringTestResult.attributes && scoringTestResult.attributes.length : defaultConfig.totalCount
    });
  };

  const onFilterValueChangeDebounce = filter => {
    updatePageSize(scoringTestResult, { ...defaultConfig, filter: filter });
  };

  const filterChangedDebounced = debounce(onFilterValueChangeDebounce, DEBOUNCE_DELAY);

  const onFilterValueChanged = (value, debounced) => {
    if (debounced) filterChangedDebounced(value);
    else {
      updatePageSize(scoringTestResult, { ...defaultConfig, filter: value });
    }
  };

  const filterByValue = (scoringAttributes, filter) => {
    const result = scoringAttributes.filter(attr => attr.attributeDescription.toLowerCase().indexOf(filter.toLowerCase()) !== -1);
    return result;
  };

  return (
    <EngineScoringTestComponent
      scoringTestAttributes={scoringTestAttributesOnPage}
      scoringTestResponse={scoringTestResult && scoringTestResult.responseScore}
      paginationAndFilter={paginationAndFilter}
      rowsPerPageOptions={rowsPerPageOptions}
      onRowsPerPageChange={onRowsPerPageChange}
      onPageChange={onPageChange}
      onRefresh={onPaginationAndFiltersRefresh}
      onFilterValueChanged={onFilterValueChanged}
    />
  );
};

EngineScoringTestContainer.propTypes = {
  engineScoringId: PropTypes.number.isRequired
};

export default EngineScoringTestContainer;
